import { ArrowRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import ProtectedRoute from '@/components/ProtectedRoute';
import { useLanguage } from '@/contexts/LanguageContext';

const Homepage = () => {
  const { locale } = useLanguage();
  const french = locale.startsWith('fr');
  const copy = french ? {
    audience: 'POUR LES COACHS & LES ÉCOLES',
    description: 'Vos cours, vos élèves, vos factures. Un seul endroit.',
    start: 'Créer mon compte',
    login: 'Se connecter',
    features: ['Agenda & réservation des cours', 'Gestion des élèves & des parents', 'Facturation & suivi des paiements', 'Messages & communication'],
    footnote: 'Moins de gestion. Plus de temps pour enseigner.',
    contact: 'Contact',
    legal: 'Mentions légales',
  } : {
    audience: 'FOR COACHES & SCHOOLS',
    description: 'Your lessons, students, and invoices. All in one place.',
    start: 'Create an account',
    login: 'Sign in',
    features: ['Scheduling & lesson bookings', 'Student & parent management', 'Invoicing & payment tracking', 'Messages & communication'],
    footnote: 'Less admin. More time to teach.',
    contact: 'Contact',
    legal: 'Legal',
  };

  return (
    <div className="poppy-home flex min-h-screen flex-col bg-background text-foreground">
      <header className="px-6 py-6 sm:px-10 sm:py-8">
        <Link to="/" aria-label="ThePoppyApp" className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-opacity hover:opacity-80">
          <img src="/logo_poppy.png" alt="The Poppy App" className="h-8 w-auto" />
        </Link>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-6 pb-16 pt-10 text-center sm:pb-24 sm:pt-12">
        <div className="w-full max-w-2xl motion-safe:animate-fade-in">
          <p className="mb-6 text-[11px] font-medium tracking-normal text-muted-foreground">{copy.audience}</p>
          <h1 className="break-words font-display text-[42px] font-semibold leading-[1.15] tracking-normal sm:text-6xl lg:text-7xl">ThePoppyApp<span className="text-accent">.</span></h1>
          <p className="mx-auto mt-5 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg">{copy.description}</p>

          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="h-12 w-full rounded-md px-6 sm:w-auto">
              <Link to="/register">{copy.start}<ArrowRight aria-hidden="true" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-12 w-full rounded-md px-6 hover:bg-muted hover:text-foreground sm:w-auto">
              <Link to="/login">{copy.login}</Link>
            </Button>
          </div>

          <div className="mx-auto mt-14 max-w-lg border-t border-border pt-7">
            <ul className="grid grid-cols-1 gap-x-8 gap-y-3 text-left text-xs text-muted-foreground sm:grid-cols-2">
              {copy.features.map(feature => (
                <li key={feature} className="flex items-center gap-2.5 leading-relaxed">
                  <Check aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-foreground/60" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-8 text-xs text-muted-foreground">{copy.footnote}</p>
        </div>
      </main>

      <footer className="flex flex-wrap items-center justify-between gap-4 px-6 py-6 text-[11px] text-muted-foreground sm:px-10">
        <span>© {new Date().getFullYear()} ThePoppyApp</span>
        <nav aria-label={french ? 'Informations' : 'Information'} className="flex items-center gap-5">
          <Link to="/contact" className="transition-colors hover:text-foreground">{copy.contact}</Link>
          <Link to="/legal" className="transition-colors hover:text-foreground">{copy.legal}</Link>
        </nav>
      </footer>
    </div>
  );
};

const Index = () => (
  <ProtectedRoute redirectAuthenticated={true} redirectPath="/agenda">
    <Homepage />
  </ProtectedRoute>
);

export default Index;